import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { classDerivedDeviceEnabledTypes, getChildEdgeTypeId } from '@iotv/datamodel';
import { AdjacentType, BranchingQueryDefinition, edgeTypeMatrix, getParentEdgeTypeId, getSpecificEdgeTypeId } from '@iotv/datamodel-core';
import { AppValueTypes, ComparisonType, LogicalOperatorType } from '@iotv/iotv-v3-types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Stack, Tab } from '@mui/material';
import React, { useState } from 'react';
import { getAdjacentFromStateOrFetch } from '../../../helpers/applicationDataHelpers';
import { useMostProximateCustomer } from '../../../hooks/useMostProximateCustomer';
import { useTypedSearchParams } from '../../../hooks/useTypedSearchParams';
import { ComponentMode, ShowDefinedEntriesOnlyOpts, } from '../../../types/AppTypes';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { ObjectHeader } from '../../../v2/components/generic/ObjectView/ObjectHeader';
import { getViewDefintionFromObject } from '../../factories/AttributeDefinitions';
import { Listerlizer } from '../../factories/Listerlizer/Listerlizer';
import { PartnerUserDetailCard } from '../Users/PartnerUserDetailCard';
const debug = process.env.REACT_APP_DEBUG && false;
const badObject = { id: 'badObj', pk: 'Group:idk', sk: 'Group:idk', type: 'Group', supportedType: 'Customer' };
function UserCustomerGroupView(props) {
    const { viewObject, transactionFunctions, contextCustomer, applicationData, userFunctions, allowedActions } = props;
    const contextObject = viewObject.matchedPrimary ?? badObject;
    const mostProximateCustomer = useMostProximateCustomer({ item: contextObject });
    const supportedType = contextObject.supportedType;
    const readableSupportedType = readableTypeName(supportedType);
    const pluralSupportedType = pluralReadableTypeName(supportedType);
    // If a groups parent is Partner and the supported type is User, we're in a Partner User Group and must manipulate the add User dialog
    const parentPartners = getAdjacentFromStateOrFetch({
        contextObject,
        adjacentFilter: {
            adjacentType: AdjacentType.PARENT,
            objectType: 'Partner',
        },
        userFunctions,
        applicationData,
    });
    const parentPartner = supportedType === 'User' ? (parentPartners[0] ?? null) : null;
    const initViewDefinition = contextObject
        ? getViewDefintionFromObject(contextObject)
        : {
            name: {
                key: 'name',
                editable: false,
                label: 'name',
                precision: undefined,
                stateFn: undefined,
                type: AppValueTypes.STRING,
                unit: undefined,
                validationRx: undefined,
            },
        };
    const [groupViewDefinition, setGroupViewDefinition] = useState(initViewDefinition);
    const [searchParams, setSearchParams] = useTypedSearchParams();
    const [value, setValue] = useState(supportedType);
    let containsAll = false;
    if (contextObject.type === 'Group') {
        if (contextObject.name === `All ${contextObject.supportedType}s`) {
            containsAll = true;
        }
    }
    const { action, relatedObjectTypeId, relatedObjectSk } = searchParams;
    const edgeTypeId = getChildEdgeTypeId(contextObject.type, contextObject.supportedType);
    const groupToCustomerAdjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: supportedType,
        edgeFilter: { edgeTypeId: edgeTypeId },
    };
    const groupToGroupAdjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: 'Group',
        edgeFilter: { edgeTypeId: edgeTypeMatrix.Group.Group.group_has_subGroup.edgeTypeId },
    };
    const groupAllowsGroupAdjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: 'Group',
        edgeFilter: { edgeTypeId: getSpecificEdgeTypeId('Group', 'Group', 'group_allows_group') },
    };
    const GetEntityGroupsAdjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: 'Group',
        filterConfig: [
            {
                filters: classDerivedDeviceEnabledTypes.map((entityKey) => {
                    return { key: 'supportedType', value: entityKey, predicate: ComparisonType.EQUALS };
                }),
                setOperation: LogicalOperatorType.OR,
            },
        ],
    };
    const getEntityGroupsBranchingQueryDef = new BranchingQueryDefinition({
        id: 'getEntityGroups',
        path: classDerivedDeviceEnabledTypes.map((det) => ({
            objectTypeId: 'Group',
            adjacencyType: AdjacentType.CHILD,
            nodeFilters: [
                {
                    setOperation: LogicalOperatorType.AND,
                    filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: det }],
                },
            ],
            edgeTypeId: getParentEdgeTypeId('Group', 'Customer'),
        })),
        subQueries: [],
    });
    debug && console.log('match', { action, relatedObjectTypeId, relatedObjectSk });
    const custGroupViewDefinition = {
        name: {
            key: 'name',
            label: 'Name',
            type: AppValueTypes.STRING,
            editable: true,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
        description: {
            key: 'description',
            label: 'Description',
            type: AppValueTypes.STRING,
            editable: true,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
        supportedType: {
            key: 'supportedType',
            label: 'Supported Type',
            type: AppValueTypes.STRING,
            editable: false,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
    };
    const custViewDefinition = {
        name: {
            key: 'name',
            label: 'Name',
            type: AppValueTypes.STRING,
            editable: false,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
        description: {
            key: 'description',
            label: 'Description',
            type: AppValueTypes.STRING,
            editable: false,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
        phoneNumber: {
            key: 'phoneNumber',
            label: 'Phone number',
            type: AppValueTypes.STRING,
            editable: false,
            unit: undefined,
            precision: undefined,
            stateFn: undefined,
            validationRx: undefined,
        },
    };
    debug && console.log(`CustomerGroupView using`, { contextCustomer, contextObject });
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: contextObject }), _jsxs(Stack, { gap: 1, children: [contextObject && (_jsx(ObjectDetailCard
                    //deletable={!containsAll}
                    , { 
                        //deletable={!containsAll}
                        matchedPrimary: contextObject, mostProximateCustomer: contextCustomer, applicationData: applicationData, 
                        //editMode={ComponentMode.EDIT}
                        allowedActions: allowedActions ? { ...allowedActions, delete: containsAll ? false : allowedActions.delete } : undefined, userFunctions: userFunctions, transactionFunctions: transactionFunctions, viewDefinition: custGroupViewDefinition, definedEntriesOnlyOpt: ShowDefinedEntriesOnlyOpts.Yes })), _jsxs(TabContext, { value: value, children: [_jsxs(TabList, { onChange: (e, newValue) => setValue(newValue), children: [_jsx(Tab, { label: pluralSupportedType, value: supportedType }), _jsx(Tab, { label: 'Sub Groups', value: 'subGroups' }), supportedType === 'User' && _jsx(Tab, { label: 'Visible Groups', value: 'visibleGroups' })] }), _jsx(TabPanel, { value: supportedType, sx: { padding: 0, paddingBlockStart: 0 }, children: contextCustomer !== undefined && (_jsx(Stack, { gap: 1, children: _jsx(Listerlizer, { viewObject,
                                        contextObject,
                                        mostProximateCustomer,
                                        canaryString: `CustomerGroupView ${supportedType}s In Group`,
                                        adjacentFilter: groupToCustomerAdjacentFilter, initMode: ComponentMode.EDIT, userFunctions: userFunctions, viewDefinition: custViewDefinition, transactionFunctions: transactionFunctions, searchRootObjects: [contextCustomer], applicationData: applicationData, AddComponentOverride: parentPartner ? (p) => _jsx(PartnerUserDetailCard, { ...p }) : undefined }) })) }), _jsx(TabPanel, { value: 'subGroups', sx: { padding: 0, paddingBlockStart: 0 }, children: _jsx(Stack, { gap: 1, children: _jsx(Listerlizer, { viewObject,
                                        canaryString: `CustomerGroupView ${supportedType} Groups In Group`,
                                        contextObject,
                                        mostProximateCustomer,
                                        adjacentFilter: groupToGroupAdjacentFilter,
                                        initialSort: [{ field: 'name', sort: 'asc' }], initMode: ComponentMode.EDIT, userFunctions: userFunctions, viewDefinition: groupViewDefinition, transactionFunctions: transactionFunctions, searchRootObjects: undefined, applicationData: applicationData }) }) }), _jsx(TabPanel, { value: 'visibleGroups', sx: { padding: 0, paddingBlockStart: 0 }, children: _jsx(Stack, { gap: 1, children: _jsx(Listerlizer, { viewObject,
                                        canaryString: `UserCustomerGroupView ${supportedType} Groups In Group`,
                                        contextObject,
                                        mostProximateCustomer,
                                        adjacentFilter: groupAllowsGroupAdjacentFilter,
                                        //searchAdjacentFilter: GetEntityGroupsAdjacentFilter,
                                        linkableItemsBranchingQueryDefinition: getEntityGroupsBranchingQueryDef,
                                        userFunctions,
                                        viewDefinition: groupViewDefinition,
                                        transactionFunctions,
                                        searchRootObjects: contextCustomer ? [contextCustomer] : undefined,
                                        initMode: ComponentMode.EDIT,
                                        linkableItemsViewDefinition: custGroupViewDefinition,
                                        applicationData }) }) })] })] })] }));
}
export default UserCustomerGroupView;
